import { noveltyColours } from '../documents/noveltyColours'
import { ANALYSIS_NOT_RUN_HINT, ANALYSIS_NOT_RUN_LABEL, isPerfectScoreWithoutEvidence } from '../../utils/scoreEvidence'

export type ScoreVariant = 'confidence' | 'novelty' | 'similarity'
export type ScoreSize = 'sm' | 'md' | 'lg'

interface ScoreDisplayProps {
  score: number
  variant: ScoreVariant
  size?: ScoreSize
  label?: string
  tooltip?: string
  showLabel?: boolean
  /** How many evidence items sit behind this score. When it is 0 and the score is a perfect 100,
   *  "Analysis not run yet" is shown instead of the score. Leave unset when the evidence is not known. */
  evidenceCount?: number
}

const VARIANT_DEFAULTS: Record<ScoreVariant, { label: string; tooltip: string }> = {
  confidence: {
    label: 'Confidence',
    tooltip: 'Confidence in the AI analysis. Higher is better.',
  },
  novelty: {
    label: 'Novelty Score',
    tooltip:
      'This is the project-level aggregate score — individual documents may score higher or lower.',
  },
  similarity: {
    label: 'Similarity',
    tooltip: 'Similarity to prior art. Higher means more overlap with existing work.',
  },
}

function confidenceColours(pct: number) {
  if (pct >= 80) return { text: 'text-green-600', bg: 'bg-green-50', bar: 'bg-green-500' }
  if (pct >= 60) return { text: 'text-amber-600', bg: 'bg-amber-50', bar: 'bg-amber-500' }
  return { text: 'text-red-600', bg: 'bg-red-50', bar: 'bg-red-500' }
}

function similarityColours(pct: number) {
  // Inverted: high similarity = red (bad), low similarity = green (good)
  if (pct >= 80) return { text: 'text-red-600', bg: 'bg-red-50' }
  if (pct >= 60) return { text: 'text-amber-600', bg: 'bg-amber-50' }
  return { text: 'text-green-600', bg: 'bg-green-50' }
}

export default function ScoreDisplay({
  score,
  variant,
  size = 'md',
  label,
  tooltip,
  showLabel = true,
  evidenceCount,
}: ScoreDisplayProps) {
  const defaults = VARIANT_DEFAULTS[variant]
  const resolvedLabel = label ?? defaults.label
  const resolvedTooltip = tooltip ?? defaults.tooltip

  // Confidence is a 0-1 fraction about the analysis itself, not a novelty/similarity claim.
  if (variant !== 'confidence' && isPerfectScoreWithoutEvidence(variant === 'similarity' ? score * 100 : score, evidenceCount)) {
    return (
      <span data-testid="score-without-evidence" className="text-sm text-gray-500 italic" title={ANALYSIS_NOT_RUN_HINT}>
        {ANALYSIS_NOT_RUN_LABEL}
      </span>
    )
  }

  if (variant === 'novelty') {
    const c = noveltyColours(score)
    if (size === 'lg') {
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${c.bg}`}
          title={resolvedTooltip}
        >
          <span className={`text-lg font-bold ${c.text}`}>{score}</span>
          <span className="text-gray-500">/ 100</span>
          {showLabel && <span className="text-xs text-gray-500 ml-1">{resolvedLabel}</span>}
        </span>
      )
    }
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}
        title={resolvedTooltip}
      >
        {score}/100
        {showLabel && <span className="text-gray-500 ml-0.5">{resolvedLabel}</span>}
      </span>
    )
  }

  if (variant === 'similarity') {
    const pct = Math.round(score * 100)
    const c = similarityColours(pct)
    if (size === 'sm') {
      return (
        <span
          className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${c.bg} ${c.text}`}
          title={resolvedTooltip}
        >
          {pct}%
        </span>
      )
    }
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded-full font-medium ${c.bg} ${c.text}`}
        title={resolvedTooltip}
      >
        {pct}%
        {showLabel && <span className="text-gray-500 text-xs ml-0.5">{resolvedLabel}</span>}
      </span>
    )
  }

  // confidence: score is 0.0-1.0
  const pct = Math.round(score * 100)
  const c = confidenceColours(pct)
  if (size === 'sm') {
    return (
      <span
        className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${c.bg} ${c.text}`}
        title={resolvedTooltip}
      >
        {pct}%
      </span>
    )
  }
  return (
    <span
      data-testid="score-display-confidence"
      className={`px-2 py-1 text-sm rounded-full font-medium ${c.bg} ${c.text}`}
      title={resolvedTooltip}
    >
      {pct}% {showLabel && resolvedLabel}
    </span>
  )
}
